import { toast } from "sonner";
import { Activity, Zap as ZapIcon, Trophy, UserPlus, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { inputCls, labelCls, fmtDate } from "./shared";

function Stat({ icon: Icon, label, value, delta }) {
  return (
    <div className="bg-[#121212] border border-white/10 p-4">
      <p className="flex items-center gap-1.5 font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-2"><Icon size={12} /> {label}</p>
      <p className="font-display font-black text-2xl tracking-tight">{value ?? "—"}</p>
      {delta != null && <p className={`text-xs mt-0.5 ${delta === 0 ? "text-white/40" : delta > 0 ? "text-emerald-400" : "text-red-400"}`}>{delta > 0 ? "+" : ""}{delta} vs baseline</p>}
    </div>
  );
}

export default function OverviewPane({ athleteId, athlete, snapshot, coaches, parents, orgs, onRefresh }) {
  const s = snapshot || {};
  const d = s.baseline_deltas || {};
  const nameOf = (list, id) => list.find((x) => x.id === id)?.name || list.find((x) => x.id === id)?.email || id;

  const assignCoach = async (cid) => { if (!cid) return; try { await api.post(`/athletes/${athleteId}/coaches`, { id: cid }); toast.success("Coach assigned."); onRefresh(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };
  const removeCoach = async (cid) => { try { await api.delete(`/athletes/${athleteId}/coaches/${cid}`); onRefresh(); } catch { toast.error("Failed."); } };
  const setOrg = async (oid) => { try { await api.put(`/athletes/${athleteId}/organization`, { organization_id: oid || null }); toast.success("Organization updated."); onRefresh(); } catch { toast.error("Failed."); } };
  const assignParent = async (pid) => { if (!pid) return; try { await api.post(`/athletes/${athleteId}/parents`, { id: pid }); toast.success("Parent linked."); onRefresh(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } };

  const info = [["Sport", athlete.sport], ["Position", athlete.position], ["Age", athlete.age], ["Grad Year", athlete.grad_year], ["School", athlete.school], ["Height", athlete.height], ["Weight", athlete.weight], ["Training Phase", athlete.training_phase], ["Next Retest", fmtDate(athlete.next_retest_date)], ["Status", athlete.status]];

  return (
    <div data-testid="af-overview" className="space-y-8">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={ZapIcon} label="Speed Score" value={s.speed_score} delta={d.speed_score} />
        <Stat icon={Activity} label="Power Score" value={s.power_score} />
        <Stat icon={Trophy} label="Best 10-Yard" value={s.best_ten_yard} delta={d.ten_yard} />
        <Stat icon={Trophy} label="Best Vertical" value={s.best_vertical} delta={d.vertical} />
      </div>

      <div>
        <h2 className="font-display font-bold uppercase tracking-tight text-xl mb-4">Profile</h2>
        <div className="bg-[#121212] border border-white/10 p-6 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {info.map(([k, v]) => (
            <div key={k}><p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">{k}</p><p className="text-white/80 text-sm capitalize">{v || "—"}</p></div>
          ))}
        </div>
        {(athlete.goals || athlete.injuries || athlete.notes) && (
          <div className="bg-[#121212] border border-white/10 border-t-0 p-6 space-y-3">
            {athlete.goals && <div><p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Goals</p><p className="text-white/75 text-sm">{athlete.goals}</p></div>}
            {athlete.injuries && <div><p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Injuries</p><p className="text-white/75 text-sm">{athlete.injuries}</p></div>}
            {athlete.notes && <div><p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-white/40 mb-1">Notes</p><p className="text-white/75 text-sm">{athlete.notes}</p></div>}
          </div>
        )}
      </div>

      <div>
        <h2 className="font-display font-bold uppercase tracking-tight text-xl mb-4">Assignments</h2>
        <div className="bg-[#121212] border border-white/10 p-6 grid sm:grid-cols-3 gap-5" data-testid="af-assignments">
          <div>
            <label className={labelCls}>Add Coach</label>
            <select data-testid="af-assign-coach" onChange={(e) => { assignCoach(e.target.value); e.target.value = ""; }} defaultValue="" className={inputCls}>
              <option value="">Select coach…</option>
              {coaches.map((c) => <option key={c.id} value={c.id}>{c.name || c.email}</option>)}
            </select>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(athlete.coach_ids || []).map((c) => <button key={c} onClick={() => removeCoach(c)} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-white/20 px-2 py-1 hover:bg-red-500/20 transition-colors">{nameOf(coaches, c)} <X size={10} /></button>)}
            </div>
          </div>
          <div>
            <label className={labelCls}>Organization</label>
            <select data-testid="af-assign-org" value={athlete.organization_id || ""} onChange={(e) => setOrg(e.target.value)} className={inputCls}>
              <option value="">— None —</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Link Parent</label>
            <select data-testid="af-assign-parent" onChange={(e) => { assignParent(e.target.value); e.target.value = ""; }} defaultValue="" className={inputCls}>
              <option value="">Select parent…</option>
              {parents.map((p) => <option key={p.id} value={p.id}>{p.name || p.email}</option>)}
            </select>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {(athlete.parent_ids || []).map((p) => <span key={p} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest border border-white/20 px-2 py-1"><UserPlus size={10} /> {nameOf(parents, p)}</span>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
